import { z } from 'zod';

/** Optional filter criteria for Ticketmaster sync (key-value record). */
export const ticketmasterFilterCriteriaSchema = z.record(z.string(), z.unknown()).optional();

/** City item for Ticketmaster cities sync. */
const ticketmasterCitySchema = z
  .object({
    name: z.string(),
    stateCode: z.string().optional(),
  })
  .passthrough();

/** Coordinate item for Ticketmaster coordinates sync. */
const ticketmasterCoordinateSchema = z
  .object({
    latitude: z.number().min(-90, 'Latitude must be >= -90').max(90, 'Latitude must be <= 90'),
    longitude: z
      .number()
      .min(-180, 'Longitude must be >= -180')
      .max(180, 'Longitude must be <= 180'),
    radius: z.number().min(0, 'Radius must be non-negative').optional(),
  })
  .passthrough();

/** Request schema for POST /ticketmaster/sync/cities. */
export const citiesSyncSchema = z.object({
  cities: z.array(ticketmasterCitySchema).min(1, 'cities array is required and must not be empty'),
  maxEvents: z.number().positive().optional().default(500),
  filterCriteria: ticketmasterFilterCriteriaSchema,
});

/** Request schema for POST /ticketmaster/sync/coordinates. */
export const coordinatesSyncSchema = z.object({
  coordinates: z
    .array(ticketmasterCoordinateSchema)
    .min(1, 'coordinates array is required and must not be empty'),
  maxEvents: z.number().positive().optional().default(500),
  filterCriteria: ticketmasterFilterCriteriaSchema,
});

/** Coordinate item for custom sync (no radius). */
const customSyncCoordinateSchema = z
  .object({
    latitude: z.number().min(-90, 'Latitude must be >= -90').max(90, 'Latitude must be <= 90'),
    longitude: z
      .number()
      .min(-180, 'Longitude must be >= -180')
      .max(180, 'Longitude must be <= 180'),
  })
  .passthrough();

/** Request schema for POST /ticketmaster/sync/custom. */
export const customSyncOptionsSchema = z
  .object({
    cities: z.array(z.object({ name: z.string() }).passthrough()).optional(),
    coordinates: z.array(customSyncCoordinateSchema).optional(),
    maxEvents: z.number().positive().optional(),
    filterCriteria: ticketmasterFilterCriteriaSchema,
  })
  .refine(
    (data) => {
      const hasCities = data.cities && data.cities.length > 0;
      const hasCoordinates = data.coordinates && data.coordinates.length > 0;
      return hasCities || hasCoordinates;
    },
    {
      message: 'At least one filter (cities or coordinates) must be provided',
    },
  );
